import React from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { NoResultsFoundIcon } from "../../Icons/LivisIcons";
interface NoDataTemplateProps {
  type?: 'inspection' | 'inspector' | 'parts' | 'evaluation' | 'trash' | 'operator' | 'users' | 'roles' | 'log' | 'plan' | 'recipe' | 'manageData' | 'defectDataset' | 'archiveSettings' | 'ongoingTraining' | 'completedTraining' | 'ConfusionMetricsModal' | 'notification' | 'alertConfiguration' | 'classLibrary' | 'settingsNotification' | 'compareVersion' | 'evaluationResult' | 'colorDetection' | 'license' | 'termHistory' | 'termOrgProfile' | 'termRepo' | 'gpuHost' | 'gpuDroplets';
}
const NoDataTemplate: React.FC<NoDataTemplateProps> = ({ type = 'inspection' }) => {
  const { t } = useTranslation();
  // // Types that should render below filters/controls
  // const belowFilterTypes = ['users', 'roles'];
  // Configuration for different types
  const getContentConfig = (type: string) => {
    const configs = {
      inspection: {
        headline: t("No inspection stations found"),
        subtext: t("It looks like no stations match your selected filters.\nTry adjusting filters or add a new station.")
      },
      inspector: {
        headline: t("No AI Inspectors found"),
        subtext: t("It looks like no inspectors match your selected filters.\nTry adjusting filters or create a new AI Inspector.")
      },
      recipe: {
        headline: t("No Recipe found"),
        subtext: t("It looks like no Recipe match your selected filters.\nTry adjusting filters or create a new AI Inspector.")
      },
      parts: {
        headline: t("No parts found"),
        subtext: t("It looks like no parts match your selected filters.\nTry adjusting filters or create a new part.")
      },
      evaluation: {
        headline: t("No evaluation results found"),
        subtext: t("It looks like no results match your selected filters.\nTry adjusting filters or run a new evaluation.")
      },
      trash: {
        headline: t("No items found in trash"),
        subtext: t("It looks like nothing matches your selected filters.\nTry adjusting filters or delete items to manage them here.")
      },
      operator: {
        headline: t("No operator panels found"),
        subtext: t("It looks like no panels match your selected filters.\nTry adjusting filters or create a new operator panel.")
      },
      users: {
        headline: t("No users found"),
        subtext: t("It looks like no users match your selected filters.\nTry adjusting filters or invite a new user.")
      },
      roles: {
        headline: t("No roles found"),
        subtext: t("It looks like no roles match your selected filters.\nTry adjusting filters or create a new role.")
      },
      log: {
        headline: t("No Logs Found"),
        subtext: t("It looks like no logs match your selected filters.\nTry adjusting filters or check back later.")
      },
      plan: {
        headline: t("No Plan Found"),
        subtext: t("It looks like no plans match your selected filters.\nTry adjusting filters or create a new plan.")
      },
      manageData: {
        headline: t("No Data found"),
        subtext: t("It looks like no data match your selected filters.\nTry adjusting filters or add a new data.")
      },
      defectDataset: {
        headline: t("No Defect Dataset found"),
        subtext: t("It looks like no defect datasets match your selected filters.\nTry adjusting filters or add a new defect dataset.")
      }, 
      archiveSettings: {
        headline: t("No Data found"),
        subtext: t("It looks like no report generation requests are available.\nPlease Add a new report generation request.")
      }, 
      license: {
        headline: t("No Licenses Found"),
        subtext: t("It looks like no licenses match your selected filters.\nTry adjusting filters or create a new license.")
      },
      ongoingTraining: {
        headline: t("No active training found"),
        subtext: t("It looks like no training is running currently.\nPlease start a new training.")
      },
      completedTraining: {
        headline: t("No completed training found"),
        subtext: t("It looks like no training is completed.\nPlease start a new training.")
      } ,
      ConfusionMetricsModal: {
        headline: t("No Images Found"),
        subtext: t("There is no data to display in the confusion metrics at the moment.")
      },
      notification: {
        headline: t("No notifications found"),
        subtext: t("You have no notifications at this time."),
      },    
      alertConfiguration: {
        headline: t("No Alert Configurations Found"),
        subtext: t("It looks like no alert configurations match your selected filters.\nTry adjusting filters or create a new alert configuration.")
      },
      classLibrary: {
        headline: t("No Classes Found"),
        subtext: t("It looks like no classes match your selected filters.\nTry adjusting filters or create a class.")
      },
      settingsNotification:{
        headline: t("No Settings For This Organization Found"),
        subtext: t("It looks like no settings for this organization found.")
      },
      compareVersion: {
        headline: t("No versions to compare"),
        subtext: t("It looks like there are no versions available to compare.\nTry selecting different models or check back later when more versions are available.")
      },
      evaluationResult: {
        headline: t("No evaluation results found"),
        subtext: t("It looks like no evaluation results match your selected filters.\nTry adjusting filters or run a new evaluation.")
      },
      colorDetection: {
        headline: t("No color detection results found"),
        subtext: t("It looks like no color detection results match your selected filters.\nTry adjusting filters or run a new color detection evaluation.")
      },
      termHistory: {
        headline: t("No history found"),
        subtext: t("It looks like no history found. \nTry adjusting filters or accept new terms.")
      },
      termOrgProfile: {
        headline: t("No organization profile found"),
        subtext: t("It looks like no organization profile found. \nTry adjusting filters.")
      },
      termRepo: {
        headline: t("No Terms found"),
        subtext: t("It looks like no terms found. \nTry adjusting filters or create new version.")
      },
      gpuHost: {
        headline: t("No Gpu found"),
        subtext: t("It looks like no gpu found.")
      },
      gpuDroplets: {
        headline: t("No Current activities found."),
        subtext: t("To know more information switch to past activities")
      }
    };
    return configs[type as keyof typeof configs] || configs.inspection;
  };
  const { headline, subtext } = getContentConfig(type);
  // const shouldRenderBelow = belowFilterTypes.includes(type);
  // ${shouldRenderBelow ? "livis-no-data-below-filters" : "livis-no-data-template"}
  return (
    <Box className={`
      ${type === "inspection" ||  type === "termRepo" || type === "termOrgProfile" || type === 'termHistory'  || type === "evaluationResult" || type === "users" || type === "roles" || type === "manageData" || type === "archiveSettings" || type === "license" || type === "plan" || type === 'notification' || type === 'alertConfiguration'  ? "livis-no-data-template-type-inspection" : `livis-no-data-template ${type === "trash" || type === 'classLibrary' || type === "inspector" || type === "colorDetection" || type === 'settingsNotification' || type === "ongoingTraining" || type === "recipe"  ? "livis-no-data-template-trash" : ""}`}
    `}>
      <NoResultsFoundIcon />
      <div className="livis-no-data-content">
        <p className="livis-no-data-headline">{headline}</p>
        <p className="livis-no-data-subtext">{subtext}</p>
      </div>
    </Box>
  );
};
NoDataTemplate.propTypes = {
  type: PropTypes.oneOf([
    'inspection',
    'inspector',
    'parts',
    'evaluation',
    'trash',
    'operator',
    'users',
    'roles',
    'log',
    'plan',
    'recipe',
    'manageData',
    'defectDataset',
    'archiveSettings',
    'ConfusionMetricsModal',
    'notification',
    'alertConfiguration',
    'classLibrary',
    'settingsNotification',
    'compareVersion',
    'evaluationResult',
    'colorDetection',
    'license',
    'termHistory',
    'termOrgProfile',
    'termRepo',
    'ongoingTraining',
    'gpuDroplets',
    'gpuHost'
  ]),
};
export default NoDataTemplate;